import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import * as XLSX from 'xlsx';
import { cancelEnrichment, runVerseEnrichment } from '../utils/databaseEnrich';

const ENRICHMENT_OPTIONS = {
  bibleUrl: {
    label: "Bible.com URL",
    description: "Direct NIV Bible.com link for this verse",
    tokenEstimate: 50,
  },
  reflectionQuestion: {
    label: "Reflection Question",
    description: "Open-ended, practical, non-generic question for reflection",
    tokenEstimate: 120,
  },
  tags: {
    label: "Tags",
    description: "3-5 topical keywords for categorization",
    tokenEstimate: 80,
  },
  categories: {
    label: "Categories",
    description: "2-3 broad categories for organization",
    tokenEstimate: 80,
  },
  growthArea: {
    label: "Growth Area",
    description: "One of the main personal growth areas (e.g., Spiritual, Emotional, Mental, etc.)",
    tokenEstimate: 40,
  },
  devTopic: {
    label: "Development Topic",
    description: "One key discipleship/dev topic (e.g., Identity, Prayer, Leadership)",
    tokenEstimate: 40,
  },
  concept: {
    label: "Concept",
    description: "Key theological or practical concept",
    tokenEstimate: 40,
  },
  difficultyLevel: {
    label: "Difficulty Level",
    description: "1-5 rating of verse complexity",
    tokenEstimate: 30,
  },
  relatedVerses: {
    label: "Related Verses",
    description: "2-4 related Bible verse references",
    tokenEstimate: 60,
  },
  contextualMeaning: {
    label: "Contextual Meaning",
    description: "2-3 sentences on practical application and core message",
    tokenEstimate: 200,
  },
  themeMetadata: {
    label: "Theme Scores",
    description: "Numeric scores (0-1) for emotional, spiritual, relational, etc.",
    tokenEstimate: 120,
  },
  ageRelevance: {
    label: "Age Relevance",
    description: "Scores (0-1) for each age/life stage (elementary, middle school, etc.)",
    tokenEstimate: 100,
  },
  relationshipRelevance: {
    label: "Relationship Relevance",
    description: "Scores (0-1) for each relationship status (youth, married, widowed, etc.)",
    tokenEstimate: 100,
  },
  discipleshipStageRelevance: {
    label: "Discipleship Stage Relevance",
    description: "Scores (0-1) for each discipleship stage (new believer, disciple maker, etc.)",
    tokenEstimate: 80,
  },
  christianExperienceRelevance: {
    label: "Christian Experience Relevance",
    description: "Scores (0-1) for each Christian experience (crisis, returned, etc.)",
    tokenEstimate: 80,
  },
};

const COST_PER_1K_TOKENS = {
  input: 0.01,   // $0.01 per 1K input tokens
  output: 0.03   // $0.03 per 1K output tokens
};

export const EnrichmentModal = ({ isOpen, onClose, onStart, onComplete }) => {
  const [selectedFields, setSelectedFields] = useState({
    bibleUrl: true,
    reflectionQuestion: true,
    tags: true,
    categories: true,
    growthArea: true,
    devTopic: true,
    concept: true,
    difficultyLevel: true,
    relatedVerses: true,
    contextualMeaning: true,
    themeMetadata: true,
    ageRelevance: true,
    relationshipRelevance: true,
    discipleshipStageRelevance: true,
    christianExperienceRelevance: true
  });
  
  const [enrichMode, setEnrichMode] = useState('new');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentVerse, setCurrentVerse] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);

  // Reset states when modal is opened
  useEffect(() => {
    if (isOpen) {
      setIsProcessing(false);
      setIsCancelling(false);
      setProgress(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleEnrich = async () => {
    setIsProcessing(true);
    onStart?.();
    try {
      const result = await runVerseEnrichment(
        (progress, verseRef) => {
          setProgress(progress);
          setCurrentVerse(verseRef || '');
        },
        selectedFields,
        enrichMode
      );
      alert(`Enrichment complete!\nProcessed ${result.updated} verses\nCost: $${result.finalCost}`);
    } catch (error) {
      if (error.message !== 'Cancelled') {
        alert(`Error during enrichment: ${error.message}`);
      }
    } finally {
      setIsProcessing(false);
      setIsCancelling(false);
      setCurrentVerse('');
      onComplete?.();
      onClose();
    }
  };

  const handleCancel = async () => {
    if (isProcessing) {
      setIsCancelling(true);
      await cancelEnrichment();
    } else {
      onClose();
    }
  };

  const handleClose = () => {
    if (isProcessing) {
      if (window.confirm('Enrichment is in progress. Are you sure you want to cancel?')) {
        handleCancel();
      }
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4">Enrich Verse Data <span className="text-sm text-gray-500">(using OpenAI)</span></h2>
        
        {/* Enrichment Mode Selection */}
        <div className="mb-4">
          <h3 className="font-semibold mb-2">Enrichment Mode</h3>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="new"
                checked={enrichMode === 'new'}
                onChange={(e) => setEnrichMode(e.target.value)}
                className="mr-2"
              />
              Enrich newly uploaded/empty verses only
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="all"
                checked={enrichMode === 'all'}
                onChange={(e) => setEnrichMode(e.target.value)}
                className="mr-2"
              />
              Enrich all verses (overwrites existing data)
            </label>
          </div>
        </div>

        {/* Field Selection */}
        <div className="mb-4">
          <h3 className="font-semibold mb-2">Fields to Enrich</h3>
          {/* Group: Main Fields */}
          <div className="mb-2">
            {Object.entries(ENRICHMENT_OPTIONS).filter(([key]) => !key.endsWith('Relevance')).map(([key, option]) => (
            <label key={key} className="flex items-center mt-2">
              <input
                type="checkbox"
                checked={selectedFields[key]}
                onChange={(e) => setSelectedFields(prev => ({
                  ...prev,
                  [key]: e.target.checked
                }))}
                className="mr-2"
              />
              <div>
                <div className="font-medium">{option.label}</div>
                <div className="text-sm text-gray-500">{option.description}</div>
              </div>
            </label>
          ))}
          </div>
          {/* Group: Relevance Fields */}
          <div className="mt-4 p-3 rounded bg-blue-50 border border-blue-200">
            <div className="font-semibold mb-1 text-blue-700">Relevance Fields</div>
            {Object.entries(ENRICHMENT_OPTIONS).filter(([key]) => key.endsWith('Relevance')).map(([key, option]) => (
              <label key={key} className="flex items-center mt-2">
                <input
                  type="checkbox"
                  checked={selectedFields[key]}
                  onChange={(e) => setSelectedFields(prev => ({
                    ...prev,
                    [key]: e.target.checked
                  }))}
                  className="mr-2"
                />
                <div>
                  <div className="font-medium">{option.label}</div>
                  <div className="text-sm text-blue-600">{option.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-2">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={isCancelling}
          >
            {isCancelling ? 'Cancelling...' : isProcessing ? 'Stop' : 'Cancel'}
          </button>
          <button
            onClick={handleEnrich}
            disabled={isProcessing || isCancelling}
            className={`px-4 py-2 rounded ${
              isProcessing || isCancelling
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            {isCancelling ? 'Cancelling...' : isProcessing ? 'Processing...' : 'Start Enrichment'}
          </button>
        </div>

        {/* Progress Bar */}
        {isProcessing && (
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                className="bg-blue-600 h-2.5 rounded-full" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-center mt-2">
              {isCancelling ? 'Cancelling...' : `${progress}% Complete`}
            </p>
            {currentVerse && (
              <p className="text-center text-xs text-gray-500 mt-1">Processing: <span className="font-mono">{currentVerse}</span></p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
